import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '../../lib/utils';

function Dialog({
  modal = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" modal={modal} {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        // Soft scrim so page content stays visible behind the modal (esp. dark mode).
        'tw-fixed tw-inset-0 tw-z-50 tw-bg-black/40 dark:tw-bg-black/30',
        className
      )}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
      }}
      {...props}
    />
  );
});
DialogOverlay.displayName = 'DialogOverlay';

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onInteractOutside,
  onEscapeKeyDown,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  onInteractOutside?: (event: Event) => void
  onEscapeKeyDown?: (event: KeyboardEvent) => void
}) {
  // Check if className already contains a max-width class
  const hasMaxWidth = className && /tw-max-w-/.test(className);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          `dialog-content tw-bg-card tw-text-card-foreground tw-border tw-border-border tw-z-50
          tw-fixed tw-top-1/2 tw-left-1/2
          tw-max-h-[calc(100vh-96px)] tw-w-full
          tw-rounded-lg tw-shadow-lg tw-flex tw-flex-col`,
          !hasMaxWidth && 'tw-max-w-[calc(100%-2rem)]',
          'tw-translate-x-[-50%] tw-translate-y-[-50%]',
          className
        )}
        // className="tw-translate-x-[-50%] tw-translate-y-[-50%]"
        onInteractOutside={onInteractOutside}
        onEscapeKeyDown={onEscapeKeyDown}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
        }}
        {...props}
      >
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className={cn(
              'tw-absolute tw-top-4 tw-right-4 tw-cursor-pointer tw-transition-colors tw-z-10 tw-border-none',
              'tw-bg-muted tw-text-muted-foreground tw-rounded-full tw-h-8 tw-w-8 tw-p-2',
              'hover:tw-bg-accent hover:tw-text-accent-foreground',
              'focus:tw-outline-none',
              'disabled:tw-pointer-events-none'
            )}
          >
            <XIcon className="tw-w-4 tw-h-4" />
            <span className="tw-sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
        <div className="tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden tw-min-h-0">
          {children}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('dialog-header tw-flex tw-flex-col tw-gap-2 tw-flex-shrink-0 ', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'tw-flex tw-flex-col-reverse tw-gap-2 sm:tw-flex-row sm:tw-justify-end',
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('tw-text-lg tw-leading-6 tw-font-semibold tw-text-card-foreground tw-m-0', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('tw-text-muted-foreground tw-text-base', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
